/**
 * Creating a custom button here for "toggle" button behavior. Styling is different than our existing buttons
 * so I've used a div with button implementation so I don't have to reset styles or have regression issues.
 * Button implementation requirements can be found here: https://www.w3.org/WAI/ARIA/apg/example-index/button/button.html
 */

import type { ComponentProps, JSX, PropsWithChildren } from 'react';

import { Box } from '@change-corgi/design-system-legacy/layout';
import { sunriseGradientBorder } from '@change-corgi/design-system-legacy/theme';

const ToggleButtonStyles = {
	alignItems: 'center',
	borderRadius: 'big',
	boxShadow: 'low',
	display: 'flex',
	justifyContent: 'center',
	padding: 'sunrise.10',
	'& svg path': {
		fill: 'sunrise.semantic.icon.disabled',
	},
	'&:hover': {
		backgroundColor: 'sunrise.semantic.fill.hovered.weak',
		cursor: 'pointer',
	},
	'&[aria-pressed=true]': {
		boxShadow: 'elevatedSelected',
		...sunriseGradientBorder,
		'&[aria-pressed=true] svg path': {
			fill: 'sunrise.gradient.icon',
		},
	},
};

type ChangeEvent = React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>;
export type ToggleButtonProps<VALUE> = Omit<ComponentProps<typeof Box>, 'onChange'> & {
	onChange?: (e: ChangeEvent, value: VALUE) => void;
	value: VALUE;
	selected?: boolean;
};
export function ToggleButton<VALUE>({
	children,
	onChange,
	selected,
	value,
	...rest
}: PropsWithChildren<ToggleButtonProps<VALUE>>): JSX.Element {
	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!onChange) return;

		switch (e.key) {
			case 'Enter': {
				e.preventDefault();
				onChange(e, value);
				break;
			}
			// The default space key behavior is to scroll the page, so we should
			// prevent the default behavior and activate the action handler on keyup instead
			// to mimic native button events
			case ' ':
			case 'Spacebar': {
				e.preventDefault();
				break;
			}
			default:
				break;
		}
	};

	const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!onChange) return;

		switch (e.key) {
			case ' ':
			case 'Spacebar': {
				e.preventDefault();
				onChange(e, value);
				break;
			}
			default:
				break;
		}
	};

	const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!onChange) return;

		onChange(e, value);
	};

	return (
		// eslint-disable-next-line @change-corgi/require-data-qa-attribute -- consumer handles data-qa attributes
		<Box
			role="button"
			aria-pressed={selected}
			tabIndex={0}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			sx={{ ...ToggleButtonStyles }}
			{...rest}
		>
			{children}
		</Box>
	);
}
